import { useCallback, useMemo } from 'react';
import { Claims, FeatureFlag } from '../constants';
import { useFeatureFlags } from './useFeatureFlag';

/**
 * Hook: `useClaimVisibilityRules`
 *
 * This hook provides a mechanism to determine if a specific claim is active and visible
 * to the user, based on predefined rules and feature flags. It uses React's `useMemo` and `useCallback`
 * to optimize performance and ensure that the rules are computed only when dependencies change.
 *
 * ### Dependencies:
 * - `useFeatureFlags`: A custom hook that provides the `isFeatureEnabled` function to check the status of feature flags.
 * - `Claims`: An enumeration of all possible claims.
 * - `FeatureFlag`: An enumeration of feature flags used to conditionally enable or disable claims.
 *
 * ### Returns:
 * - `isClaimActive`: A function that takes a claim as input and returns a boolean indicating whether the claim is active and visible.
 *
 * ### Input:
 * - `claim` (Claims): The claim to check.
 *
 * ### Output:
 * - `boolean`: Returns `true` if the claim is active and visible to the user based on the rules, otherwise `false`.
 *
 * ### Example usage:
 * ```javascript
 * import { useClaimVisibilityRules } from './hooks/useClaimVisibilityRules';
 * import { Claims } from './constants';
 *
 * const MyComponent = () => {
 *   const { isClaimActive } = useClaimVisibilityRules();
 *
 *   const canReadTickets = isClaimActive(Claims.TICKETS_READ);
 *   const canWritePayments = isClaimActive(Claims.PAYMENTS_WRITE);
 *
 *   return (
 *     <div>
 *       {canReadTickets && <p>The Claim TICKETS_READ is active and visible to the user.</p>}
 *       {canWritePayments && <p>The Claim PAYMENTS_WRITE is active and visible to the user.</p>}
 *     </div>
 *   );
 * };
 * ```
 */
export const useClaimVisibilityRules = () => {
  const { isFeatureEnabled } = useFeatureFlags();

  const claimVisibilityRules: Record<`${Claims}`, () => boolean> = useMemo(
    () => ({
      [Claims.ORGANIZATION_READ]: () => isFeatureEnabled(FeatureFlag.ORGANIZATION) || false,
      [Claims.ORGANIZATION_WRITE]: () => isFeatureEnabled(FeatureFlag.ORGANIZATION) || false,
      [Claims.PEOPLE_READ]: () => isFeatureEnabled(FeatureFlag.PEOPLE) || false,
      [Claims.DASHBOARD_READ]: () => isFeatureEnabled(FeatureFlag.DASHBOARD) || false,
      [Claims.ASPECT_READ]: () => isFeatureEnabled(FeatureFlag.ASPECT) || false,
      [Claims.SURVEYS_READ]: () => isFeatureEnabled(FeatureFlag.SURVEY) || false,
      [Claims.SURVEYS_WRITE]: () => isFeatureEnabled(FeatureFlag.SURVEY) || false,
      [Claims.DEPARTMENTS_READ]: () => isFeatureEnabled(FeatureFlag.DEPARTMENTS) || false,
      [Claims.DEPARTMENT_READ]: () => isFeatureEnabled(FeatureFlag.DEPARTMENT) || false,
      [Claims.TRAINING_READ]: () => isFeatureEnabled(FeatureFlag.TRAINING) || false,
      [Claims.PROFILE_READ]: () => isFeatureEnabled(FeatureFlag.PROFILE) || false,
      [Claims.BILLING_READ]: () => true,
      [Claims.BILLING_WRITE]: () => true,
    }),
    [isFeatureEnabled],
  );

  const isClaimActive = useCallback(
    (claim: Claims) => {
      if (!claimVisibilityRules?.[claim]) {
        return false;
      }
      const claimStatus = claimVisibilityRules?.[claim]?.();
      return claimStatus;
    },
    [claimVisibilityRules],
  );

  return { isClaimActive };
};
